/**
 * Trailhead chat-page diagnostics — front-end console instrumentation
 * for `/trailhead/persona/:id/:sid` and downstream.
 *
 * Toggle (any of):
 *   1. Default ON in `process.env.NODE_ENV === 'development'`
 *   2. `localStorage.setItem('trailhead.diag', '1')` — persists across reloads
 *   3. `localStorage.setItem('trailhead.diag', '0')` — disables (overrides dev default)
 *   4. `window.__TRAILHEAD_DIAG__ = true | false` — runtime override (highest priority)
 *
 * On TrailheadCore mount the helper attaches `window.trailheadDiag`:
 *   trailheadDiag.enable()   → localStorage flag '1'
 *   trailheadDiag.disable()  → localStorage flag '0'
 *   trailheadDiag.clear()    → removes the flag (falls back to dev default)
 *   trailheadDiag.enabled()  → boolean
 *
 * Surface taxonomy (tag → color):
 *   core      cyan     TrailheadCore page
 *   layout    emerald  TrailheadLayout composition shell
 *   header    violet   TrailheadHeader top band
 *   workshop  amber    WorkshopRow open/close + sheet
 *   chips     pink     ChipManager / PersonaChipDefaults / chip wire
 *   surface   indigo   PersonaConversationSurface
 *   strip     blue     ContinueTrailStrip
 *   compose   lime     ComposePanel (light touch only)
 *   picker    teal     TrailheadPicker (landing surface)
 */

import { useEffect, useRef } from 'react';

const ENABLED_KEY = 'trailhead.diag';

let cachedEnabled: boolean | null = null;
let cachedAt = 0;
const CACHE_TTL_MS = 500; // re-check every 500ms so toggles take effect quickly

export type DiagSurface =
  | 'core'
  | 'layout'
  | 'header'
  | 'workshop'
  | 'chips'
  | 'surface'
  | 'strip'
  | 'compose'
  | 'picker';

const SURFACE_COLORS: Record<DiagSurface, string> = {
  core: '#06b6d4', // cyan
  layout: '#10b981', // emerald
  header: '#8b5cf6', // violet
  workshop: '#f59e0b', // amber
  chips: '#ec4899', // pink
  surface: '#6366f1', // indigo
  strip: '#3b82f6', // blue
  compose: '#84cc16', // lime
  picker: '#14b8a6', // teal
};

function readEnabledRaw(): boolean {
  if (typeof window === 'undefined') return false;
  // Window override is highest priority.
  const w = (window as any).__TRAILHEAD_DIAG__;
  if (w === true) return true;
  if (w === false) return false;
  // localStorage flag.
  try {
    const ls = window.localStorage?.getItem(ENABLED_KEY);
    if (ls === '1') return true;
    if (ls === '0') return false;
  } catch {
    /* localStorage unavailable */
  }
  // Default: ON in dev, OFF in prod.
  return process.env.NODE_ENV === 'development';
}

export function isDiagEnabled(): boolean {
  const now = Date.now();
  if (cachedEnabled !== null && now - cachedAt < CACHE_TTL_MS) {
    return cachedEnabled;
  }
  cachedEnabled = readEnabledRaw();
  cachedAt = now;
  return cachedEnabled;
}

export function setDiagEnabled(on: boolean | null): void {
  cachedEnabled = null; // invalidate
  if (typeof window === 'undefined') return;
  try {
    if (on === null) window.localStorage.removeItem(ENABLED_KEY);
    else window.localStorage.setItem(ENABLED_KEY, on ? '1' : '0');
  } catch {
    /* localStorage unavailable */
  }
}

export interface DiagPayload {
  surface: DiagSurface;
  event: string;
  detail?: Record<string, unknown> | unknown[] | string | number | boolean | null;
}

/** Single emit. Cheap fast-path when disabled. */
export function diag(p: DiagPayload): void {
  if (!isDiagEnabled()) return;
  const color = SURFACE_COLORS[p.surface] ?? '#9ca3af';
  // eslint-disable-next-line no-console
  console.log(
    `%c[trailhead/${p.surface}]%c ${p.event}`,
    `color: ${color}; font-weight: 600`,
    'color: inherit',
    p.detail !== undefined ? p.detail : '',
  );
}

/** Fires once on mount, once on unmount. */
export function useDiagMount(
  surface: DiagSurface,
  label: string,
  detail?: Record<string, unknown>,
): void {
  // Capture detail snapshot at mount-time for unmount log.
  const snapshotRef = useRef(detail);
  snapshotRef.current = detail;

  useEffect(() => {
    diag({ surface, event: `mount · ${label}`, detail: snapshotRef.current });
    return () => {
      diag({ surface, event: `unmount · ${label}`, detail: snapshotRef.current });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

/**
 * Logs whenever any value in `watched` changes. Tag with a short `event`
 * label and the helper logs the new shape. First render is logged too so
 * the initial state is captured in the console scrollback.
 */
export function useDiagWatch(
  surface: DiagSurface,
  event: string,
  watched: Record<string, unknown>,
): void {
  // Track previous values so the diff log is meaningful.
  const prevRef = useRef<Record<string, unknown> | null>(null);
  useEffect(() => {
    const prev = prevRef.current;
    if (prev === null) {
      diag({ surface, event: `${event} · init`, detail: watched });
    } else {
      const changed: Record<string, { from: unknown; to: unknown }> = {};
      for (const key of Object.keys(watched)) {
        if (!Object.is(prev[key], watched[key])) {
          changed[key] = { from: prev[key], to: watched[key] };
        }
      }
      if (Object.keys(changed).length > 0) {
        diag({ surface, event, detail: changed });
      }
    }
    prevRef.current = { ...watched };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, Object.values(watched));
}

/**
 * ResizeObserver wrapper. Logs `{w, h}` whenever the observed element's
 * box changes. Fires once on mount with initial dimensions.
 */
export function useLayoutDiag(
  ref: React.RefObject<HTMLElement | null>,
  surface: DiagSurface,
  label: string,
): void {
  useEffect(() => {
    if (!isDiagEnabled()) return;
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const log = () => {
      const rect = el.getBoundingClientRect();
      diag({
        surface,
        event: `layout · ${label}`,
        detail: { w: Math.round(rect.width), h: Math.round(rect.height) },
      });
    };
    log();
    const ro = new ResizeObserver(log);
    ro.observe(el);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

/**
 * Attach `window.trailheadDiag` once. Call from a top-level component
 * (TrailheadCore mount) so DevTools users have toggles available.
 */
export function installDiagWindow(): void {
  if (typeof window === 'undefined') return;
  if ((window as any).trailheadDiag) return;
  (window as any).trailheadDiag = {
    enable: () => {
      setDiagEnabled(true);
      // eslint-disable-next-line no-console
      console.log('%c[trailhead/diag]%c enabled — reload to populate scrollback', 'color: #10b981; font-weight:600', 'color: inherit');
    },
    disable: () => {
      setDiagEnabled(false);
      // eslint-disable-next-line no-console
      console.log('%c[trailhead/diag]%c disabled', 'color: #ef4444; font-weight:600', 'color: inherit');
    },
    clear: () => {
      setDiagEnabled(null);
      // eslint-disable-next-line no-console
      console.log('%c[trailhead/diag]%c flag cleared (defaults to dev mode)', 'color: #9ca3af; font-weight:600', 'color: inherit');
    },
    enabled: () => isDiagEnabled(),
  };
}
